/* ─────────────────────────────────────────────────────────────────
   MLW Quiz Persist — Metaobject persistence + shareable results URL
   ─────────────────────────────────────────────────────────────────
   Mints a unique ID per quiz submission and POSTs the payload to a
   backend proxy that writes it to a Shopify Metaobject
   (`quiz_result`). The proxy endpoint is configurable; the ID
   becomes the `?id=` of the shareable results URL.

   Why a proxy endpoint rather than Storefront API directly?
     • Storefront API cannot write metaobjects.
     • Admin API requires a secret; can't expose to the browser.
     • A tiny serverless function (Oxygen / Cloud Run / Vercel) is
       the minimum viable write path. This module doesn't care
       which — it just needs a URL that accepts POST JSON and
       returns { id } (or whatever shape the caller configures).

   If persist fails for ANY reason we never block the quiz — the
   result UI still renders. We just don't mint a shareable URL.

   PUBLIC API
     MLWQuizPersist.init(config)
     MLWQuizPersist.persist(payload)  → Promise<{ id, url } | null>
     MLWQuizPersist.buildResultsUrl(id) → string
     MLWQuizPersist.generateId()       → string

   CONFIG
     enabled            — master on/off (MLW defaults to false)
     endpointUrl        — proxy URL that writes the metaobject
     resultsPageUrl     — base URL for the shareable link (e.g. "/results")
     idParam            — query param name (default "id")
     fetchOptions       — extra fetch init (headers, credentials, etc.)
     beforeSend(payload)→ optional mutator; returns what to POST
     parseResponse(json)→ optional; returns the id to use
     onError(err, payload)— optional callback for logging
   ───────────────────────────────────────────────────────────────── */

(function (window) {
  "use strict";

  var state = {
    enabled: false,
    endpointUrl: null,
    resultsPageUrl: "/results",
    idParam: "id",
    fetchOptions: null,
    beforeSend: null,
    parseResponse: null,
    onError: null,
  };

  /* RFC4122 v4 generator — prefers crypto.randomUUID when present,
     falls back to a Math.random()-seeded variant that's good enough
     for a non-security opaque identifier. */
  function generateId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return window.crypto.randomUUID();
      }
      if (window.crypto && window.crypto.getRandomValues) {
        var b = new Uint8Array(16);
        window.crypto.getRandomValues(b);
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        var hex = [];
        for (var i = 0; i < 16; i++) {
          var h = b[i].toString(16);
          hex.push(h.length === 1 ? "0" + h : h);
        }
        return (
          hex.slice(0, 4).join("") + "-" +
          hex.slice(4, 6).join("") + "-" +
          hex.slice(6, 8).join("") + "-" +
          hex.slice(8, 10).join("") + "-" +
          hex.slice(10, 16).join("")
        );
      }
    } catch (_e) { /* fall through to Math.random */ }
    return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function init(config) {
    config = config || {};
    state.enabled = config.enabled === true;
    state.endpointUrl = config.endpointUrl || null;
    state.resultsPageUrl = config.resultsPageUrl || "/results";
    state.idParam = config.idParam || "id";
    state.fetchOptions = config.fetchOptions || null;
    state.beforeSend = typeof config.beforeSend === "function" ? config.beforeSend : null;
    state.parseResponse = typeof config.parseResponse === "function" ? config.parseResponse : null;
    state.onError = typeof config.onError === "function" ? config.onError : null;
  }

  function buildResultsUrl(id) {
    if (!id) return state.resultsPageUrl;
    var base = state.resultsPageUrl || "/results";
    var sep = base.indexOf("?") >= 0 ? "&" : "?";
    return base + sep + encodeURIComponent(state.idParam) + "=" + encodeURIComponent(id);
  }

  /* Persist the submission. Resolves to { id, url } on success, or
     null on failure — never rejects, so callers can always show
     results regardless. */
  function persist(payload) {
    payload = payload || {};

    if (!state.enabled) {
      /* Persistence off — MLW case. No ID, no URL, no POST. */
      return Promise.resolve(null);
    }

    var id = payload.id || generateId();
    var record = {
      id: id,
      created_at: new Date().toISOString(),
      answers: payload.answers || [],
      scores: payload.scores || null,
      email: payload.email || null,
      name: payload.name || null,
      topTeas: payload.topTeas || null,
      segment: payload.segment || null,
    };
    if (payload.extra && typeof payload.extra === "object") {
      for (var k in payload.extra) {
        if (Object.prototype.hasOwnProperty.call(payload.extra, k)) record[k] = payload.extra[k];
      }
    }

    var toSend = state.beforeSend ? state.beforeSend(record) : record;

    if (!state.endpointUrl) {
      /* Enabled but no endpoint configured — mint the ID so the
         caller can still build a URL, but don't attempt to write. */
      return Promise.resolve({ id: id, url: buildResultsUrl(id) });
    }

    var opts = Object.assign(
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(toSend) },
      state.fetchOptions || {}
    );
    /* If the caller added headers via fetchOptions, merge rather than clobber. */
    if (state.fetchOptions && state.fetchOptions.headers) {
      opts.headers = Object.assign({ "Content-Type": "application/json" }, state.fetchOptions.headers);
    }

    return fetch(state.endpointUrl, opts)
      .then(function (res) {
        if (!res.ok) throw new Error("Persist failed: HTTP " + res.status);
        return res.json().catch(function () { return {}; });
      })
      .then(function (json) {
        var resolvedId = state.parseResponse ? state.parseResponse(json) : (json && json.id) || id;
        return { id: resolvedId, url: buildResultsUrl(resolvedId) };
      })
      .catch(function (err) {
        if (state.onError) { try { state.onError(err, record); } catch (_e) {} }
        else if (window.console) { console.warn("[MLWQuizPersist] persist failed:", err); }
        /* Graceful degradation: still return the minted ID so the
           caller can render results locally even though they
           weren't persisted. */
        return { id: id, url: buildResultsUrl(id), persisted: false };
      });
  }

  window.MLWQuizPersist = {
    init: init,
    persist: persist,
    buildResultsUrl: buildResultsUrl,
    generateId: generateId,
  };
})(window);
